import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Pie, PieConfig} from "@ant-design/charts";
import {useTranslation} from "react-i18next";
import {Button, Col, DatePicker, type DatePickerProps, Row, Select, Tag, Checkbox, Table} from "antd";
import {ColumnsType, TablePaginationConfig} from "antd/lib/table";
import {parseISO} from "date-fns";
import {Link} from "react-router-dom";
import {Key} from "antd/lib/table/interface";
import moment from "moment/moment";
import {Card, Label} from "../../../components/ui";
import api from "../../../services/api";
import {useToast} from "../../../hooks/toast";

import "./styles.less";
import {Filters, FiltersCard} from "../../../components/Filters";
import FormItem from "../../../components/FormItem";
import filterOptions from "../../../utils/filterOptions";
import ProjectStatus from "../../../enums/ProjectStatus";
import ProjectTypes from "../../../interfaces/IProjectType";
import {IContractor} from "../../Contractors";
import {RangePickerProps} from "antd/lib/date-picker";

const {RangePicker} = DatePicker;

interface IStat {
  count: number;
  status: string;
}

interface IProject {
  id: string;
  short_description: string;
  identifier: string;
  contractor_name: string;
  status: string;
  first_status_change: Date;
  last_status_change: Date;
  created_at: Date;
  requirements: {
    estimated_hours: number;
    wasted_hours: number;
  };
  completion_date: Date;
  expected_end: Date;
  projectType: {
    acronym: string;
  }
}

interface ICountProjectsByStatusResult {
  stats: IStat[];
  projects: IProject[];
}

const ProjectCountByStatus: React.FC = () => {
  const [filterVisible, setFilterVisible] = useState(false);
  const [contractors, setContractors] = useState<IContractor[]>([]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ICountProjectsByStatusResult>();

  // filters
  const [identifier, setIdentifier] = useState<string | undefined>(undefined);
  const [contractor, setContractor] = useState<string | undefined>(undefined);
  const [expectedEnd, setExpectedEnd] = useState<string | undefined>();
  const [notExpectedStart, setNotExpectedStart] = useState<string | undefined>();
  const [statusFiltres, setStatusFiltres] = useState<string | undefined>(
    undefined,
  );
  const [type, setType] = useState<string | undefined>(undefined);
  const [startCreatedBytMonth, setCreatedByStartMonth] = useState(`${moment().add(-5, "months").format("MM-YYYY")}`);
  const [endCreatedByMonth, setCreatedByEndMonth] = useState(
    `${moment().format("MM-YYYY")}`,
  );

  const {t} = useTranslation(["projects", "dashboards"]);
  const {addToast} = useToast();

  useEffect(() => {
    api
      .get<IContractor[]>("/contractors")
      .then(response => setContractors(response.data))
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os contratantes, tente novamente.",
        });
      });
  }, [addToast]);

  const optionsStatus = useMemo(() => {
    return (Object.keys(ProjectStatus) as (keyof typeof ProjectStatus)[]).map(
      key => {
        return {
          label: t(`status.options.${ProjectStatus[key]}.label`, {
            ns: "projects",
          }),
          value: ProjectStatus[key],
        };
      },
    );
  }, [t]);

  const contractorOptions = useMemo(() => {
    return contractors.map(ctr => {
      return {
        label: ctr.name,
        value: ctr.name,
      };
    });
  }, [contractors]);

  const optionsType = useMemo(() => {
    return (Object.keys(ProjectTypes) as (keyof typeof ProjectTypes)[]).map(
      key => {
        return {
          label: t(`project-types.options.${ProjectTypes[key]}.label`),
          value: ProjectTypes[key],
        };
      },
    );
  }, [t]);

  const identifierOption = useMemo(() => {
    if (data?.projects.length) {
      //remove identifier duplication
      return data.projects
        .map(project => project.identifier)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map(idt => {
          return {
            label: idt,
            value: idt,
          };
        });
    }
    return [];
  }, [data]);

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      defaultCurrent: 1,
      total: data?.projects.length,
      defaultPageSize: 10,
    }),
    [data],
  );

  const params = useMemo(() => {
    return {
      identifier,
      contractor_name: contractor,
      status: statusFiltres,
      type,
      start_created_at: startCreatedBytMonth,
      end_created_at: endCreatedByMonth,
      expected_end: expectedEnd,
      not_expected_start: notExpectedStart,
    };
  }, [identifier, contractor, statusFiltres, type, startCreatedBytMonth, endCreatedByMonth, expectedEnd, notExpectedStart]);

  useEffect(() => {
    setLoading(true);
    api
      .get<ICountProjectsByStatusResult>("/dashboards/projects-by-status", {
        params,
      })
      .then(response => setData(response.data))
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os projetos, tente novamente.",
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, params]);

  const stats = useMemo(
    () =>
      data?.stats.map(({status, count}) => ({
        status: t(`status.options.${status}.label`, {
          ns: "projects",
        }),
        count: Number(count),
      })) || [],
    [data?.stats, t],
  );

  const config: PieConfig = useMemo(
    () => ({
      data: stats,
      angleField: "count",
      colorField: "status",
      innerRadius: 0.6,
      // label: {
      //   type: "spider",
      //   labelHeight: 28,
      //   content: "{name}\n{percentage}",
      // },
      legend: false,
      label: undefined,
      // autoFit: true,
      height: 250,
      width: 250,
      interactions: [{type: "element-selected"}, {type: "element-active"}],
    }),
    [stats],
  );

  const projectColumns: ColumnsType<IProject> = [
    {
      title: t("identifier"),
      dataIndex: "identifier",
    },
    {
      title: t("short_description"),
      dataIndex: "short_description",
      render: (description, project: IProject) => {
        return (
          <>
            <Link to={`/app/projects/${project.id}`}>{description}</Link>
          </>
        );
      },
    },
    {
      title: t("status.label"),
      dataIndex: "status",
      render: (status: string) => {
        return (
          <Tag color={t(`status.options.${status}.color`)} key={status}>
            {t(`status.options.${status}.label`)}
          </Tag>
        );
      },
    },
    {
      title: t("type"),
      dataIndex: "type",
      render: (_: string, record) => {
        return (<Tag
          color={t(`project-types.options.${record.projectType?.acronym}.color`)}
          key={record.projectType?.acronym}
        >
          {t(`project-types.options.${record.projectType?.acronym}.label`)}
        </Tag>)
      }
    },
    {
      title: t("contractor_name"),
      dataIndex: "contractor_name",
    },
    {
      title: t("estimated_hours"),
      dataIndex: "estimated_hours",
      render: (_, record) => record.requirements.estimated_hours,
    },
    {
      title: t("wasted_hours"),
      dataIndex: "wasted_hours",
      render: (_, record) => record.requirements.wasted_hours,
    },
    {
      title: t("completion_date"),
      dataIndex: "completion_date",
      render: (date: string) =>
        date && parseISO(date).toLocaleDateString("pt-BR"),
    },
    {
      title: t("expected_end"),
      dataIndex: "expected_end",
      render: (date: string) =>
        date && moment(date).format("DD/MM/YYYY"),
    },
    {
      title: t("created_at"),
      dataIndex: "created_at",
      render: (date: string) =>
        date && parseISO(date).toLocaleDateString("pt-BR"),
    },
    {
      title: t("first_status_change"),
      dataIndex: "first_status_change",
      render: date => date && parseISO(date).toLocaleDateString("pt-BR"),
    },
    {
      title: t("last_status_change"),
      dataIndex: "last_status_change",
      render: date => date && parseISO(date).toLocaleDateString("pt-BR"),
    },
  ];

  const handleFilters = useCallback(() => {
    if (filterVisible) {
      setIdentifier(undefined);
      setContractor(undefined);
      setStatusFiltres(undefined);
      setType(undefined);
      setNotExpectedStart(undefined);
      setExpectedEnd(undefined);
    }
    setFilterVisible(old => !old);
  }, [filterVisible]);

  const onChangeDates: RangePickerProps["onChange"] = useCallback(
    (dates, _) => {
      if (dates) {
        setCreatedByStartMonth(dates[0].format("MM-YYYY"));
        setCreatedByEndMonth(dates[1].format("MM-YYYY"));
      }
    },
    [],
  );

  const onChangeExpectedStart: DatePickerProps["onChange"] = useCallback(
    (date, _) => {
      if (date) {
        setExpectedEnd(date.format("YYYY-MM-DD"));
        setNotExpectedStart(undefined);
      } else {
        setExpectedEnd(undefined);
      }
    },
    [],
  );

  const disabledDate: RangePickerProps["disabledDate"] = current => {
    // Can not select days before today and today
    return current && current > moment().endOf("month");
  };

  return (
    <Card
      loading={loading}
      title={
        <div>
          <div style={{display: "flex", justifyContent: "space-between"}}>
            <div>
              {t("projects-by-status", {ns: "dashboards"})}{" "}
              <Filters
                showFilters={filterVisible}
                setShowFilters={handleFilters}
              />
            </div>
            <div>
              <RangePicker
                onChange={onChangeDates}
                format="MMMM YYYY"
                disabledDate={disabledDate}
                picker="month"
                allowClear={false}
                value={[moment(startCreatedBytMonth, "MM-YYYY"), moment(endCreatedByMonth, "MM-YYYY")]}
              />
            </div>
          </div>
          <div>
            {filterVisible ? (
              <FiltersCard>
                <Col lg={12} md={12} sm={24} xl={9} xs={24}>
                  <div>
                    <Label color="#575962">{t("Status")}</Label>
                  </div>
                  <FormItem>
                    <Select
                      showSearch
                      allowClear
                      placeholder={t("status.placeholder")}
                      onChange={
                        status => setStatusFiltres(status)
                        // ts-ignore
                      }
                      options={optionsStatus}
                      filterOption={filterOptions}
                    />
                  </FormItem>
                </Col>
                <Col lg={12} md={12} sm={24} xl={8} xs={24}>
                  <div>
                    <Label color="#575962">{t("project-types.label")}</Label>
                  </div>
                  <FormItem>
                    <Select
                      showSearch
                      allowClear
                      placeholder={t("project-types.placeholder")}
                      onChange={
                        projectType => setType(projectType)
                        // ts-ignore
                      }
                      options={optionsType}
                      filterOption={filterOptions}
                    />
                  </FormItem>
                </Col>
                <Col lg={12} md={12} sm={24} xl={9} xs={24}>
                  <div>
                    <Label color="#575962">{t("contractor_name")}</Label>
                  </div>
                  <FormItem>
                    <Select
                      showSearch
                      allowClear
                      placeholder={t("contractor_name")}
                      onChange={
                        ctr => setContractor(ctr)
                        // ts-ignore
                      }
                      options={contractorOptions}
                      filterOption={filterOptions}
                    />
                  </FormItem>
                </Col>
                <Col lg={12} md={12} sm={24} xl={8} xs={24}>
                  <div>
                    <Label color="#575962">{t("identifier")}</Label>
                  </div>
                  <FormItem>
                    <Select
                      showSearch
                      allowClear
                      placeholder={t("identifier")}
                      onChange={
                        idt => setIdentifier(idt)
                        // ts-ignore
                      }
                      options={identifierOption}
                      filterOption={filterOptions}
                    />
                  </FormItem>
                </Col>
                <Col lg={12} md={12} sm={24} xl={8} xs={24}>
                  <div style={{
                    pointerEvents: !notExpectedStart ? 'painted' : 'none',
                    opacity: notExpectedStart ? 0.5 : 1
                  }}>
                    <div>
                      <Label color="#575962">
                        {t("expected_end")}
                      </Label>
                    </div>
                    <DatePicker
                      onChange={onChangeExpectedStart}
                      format="DD/MM/YYYY"
                      style={{width: "100%"}}
                      picker="date"
                      allowClear
                    />
                  </div>
                  <div>
                    <Label>
                      <Checkbox
                        style={{marginTop: 10, fontWeight: 'normal', color: '#575962'}}
                        onChange={e => {
                          setNotExpectedStart(e.target.checked ? "NO_END_EXPECTED" : undefined)
                        }}
                        disabled={!!expectedEnd}
                      >

                        {t("Sem Entrega Prevista")}

                      </Checkbox>
                    </Label>
                  </div>
                </Col>
              </FiltersCard>
            ) : null}
          </div>
        </div>
      }
    >
      <Row gutter={[20, 20]} align="middle">
        <Col lg={18} md={24} xl={18}>
          <Table
            className="table-striped"
            scroll={{
              x: "max-content",
              // y: maxHeight,
            }}
            dataSource={[...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || []), ...(data?.projects || [])]}
            rowKey={record => record.id}
            columns={projectColumns}
            pagination={undefined}
            size="small"
          />
        </Col>
        <Col lg={6} md={24} xl={6}>
          <Pie {...config} />
        </Col>
      </Row>
    </Card>
  );
};

export default ProjectCountByStatus;
